import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { OutlinePass } from 'three/examples/jsm/postprocessing/OutlinePass.js';

// Define default parameters for effects
const DEFAULT_EFFECT_PARAMS = {
    bloom: { strength: 0.5, radius: 0.5, threshold: 0.85 },
    outline: { edgeStrength: 3.0, edgeGlow: 0.0, edgeThickness: 1.0, pulsePeriod: 0, visibleEdgeColor: '#000000', hiddenEdgeColor: '#190a05' },
    celShader: { quantizeSteps: 4.0 },
    colorGrading: { colorBalance: new THREE.Vector3(1.0, 1.0, 1.0), saturation: 1.0, contrast: 1.0, brightness: 0.0, tint: new THREE.Vector3(0, 0, 0), pastelStrength: 0.0, pastelMatrix: [1,0,0, 0,1,0, 0,0,1] }, // Start neutral
    vignette: { intensity: 0.4, radius: 0.8, color: new THREE.Color(0x101018), softness: 0.3 }, // Slightly less intense, softer black
    filmGrain: { intensity: 0.01, size: 1.8 },
    chromaticAberration: { offset: 0.001 } // Very subtle
};

// --- Shader Definitions (kept separate for clarity) ---

const CelShader = {
    uniforms: { tDiffuse: { value: null }, uQuantizeSteps: { value: 4.0 } },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
        uniform sampler2D tDiffuse; uniform float uQuantizeSteps; varying vec2 vUv;
        void main() {
            vec4 color = texture2D(tDiffuse, vUv);
            color.rgb = floor(color.rgb * uQuantizeSteps) / uQuantizeSteps;
            gl_FragColor = color;
        }`
};

const ColorGradingShader = {
    uniforms: {
        tDiffuse: { value: null },
        uColorBalance: { value: new THREE.Vector3(1.0, 1.0, 1.0) },
        uSaturation: { value: 1.0 },
        uContrast: { value: 1.0 },
        uBrightness: { value: 0.0 },
        uTint: { value: new THREE.Vector3(0, 0, 0) },
        uPastelStrength: { value: 0.0 },
        uPastelMatrix: { value: [1,0,0, 0,1,0, 0,0,1] }
    },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
        uniform sampler2D tDiffuse; uniform vec3 uColorBalance; uniform vec3 uTint; uniform float uSaturation;
        uniform float uContrast; uniform float uBrightness; uniform float uPastelStrength; uniform float uPastelMatrix[9];
        varying vec2 vUv;
        void main() {
            vec4 color = texture2D(tDiffuse, vUv);
            color.rgb += uBrightness; color.rgb *= uColorBalance; color.rgb += uTint;
            vec3 pastelColor;
            pastelColor.r = color.r * uPastelMatrix[0] + color.g * uPastelMatrix[1] + color.b * uPastelMatrix[2];
            pastelColor.g = color.r * uPastelMatrix[3] + color.g * uPastelMatrix[4] + color.b * uPastelMatrix[5];
            pastelColor.b = color.r * uPastelMatrix[6] + color.g * uPastelMatrix[7] + color.b * uPastelMatrix[8];
            color.rgb = mix(color.rgb, pastelColor, uPastelStrength);
            float luminance = dot(color.rgb, vec3(0.299, 0.587, 0.114));
            color.rgb = mix(vec3(luminance), color.rgb, uSaturation);
            color.rgb = (color.rgb - 0.5) * uContrast + 0.5;
            color.rgb = clamp(color.rgb, 0.0, 1.0);
            gl_FragColor = color;
        }`
};

const VignetteShader = {
    uniforms: {
        tDiffuse: { value: null }, uVignetteIntensity: { value: 0.4 }, uVignetteRadius: { value: 0.8 },
        uVignetteColor: { value: new THREE.Color(0x101018) }, uVignetteSoftness: { value: 0.3 }
    },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
        uniform sampler2D tDiffuse; uniform float uVignetteIntensity; uniform float uVignetteRadius;
        uniform float uVignetteSoftness; uniform vec3 uVignetteColor; varying vec2 vUv;
        void main() {
            vec4 color = texture2D(tDiffuse, vUv);
            vec2 center = vec2(0.5); float dist = distance(vUv, center);
            float vignette = smoothstep(uVignetteRadius, uVignetteRadius - uVignetteSoftness, dist);
            color.rgb = mix(color.rgb, uVignetteColor, (1.0 - vignette) * uVignetteIntensity);
            gl_FragColor = color;
        }`
};

const FilmGrainShader = {
    uniforms: { tDiffuse: { value: null }, uTime: { value: 0 }, uGrainIntensity: { value: 0.01 }, uGrainSize: { value: 1.8 } },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
        uniform sampler2D tDiffuse; uniform float uTime; uniform float uGrainIntensity; uniform float uGrainSize; varying vec2 vUv;
        float random(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
        void main() {
            vec4 color = texture2D(tDiffuse, vUv);
            vec2 grainUv = vUv * uGrainSize; float grain = random(grainUv + vec2(uTime * 0.01)) * 2.0 - 1.0;
            color.rgb += grain * uGrainIntensity;
            gl_FragColor = color;
        }`
};

const ChromaticAberrationShader = {
    uniforms: { tDiffuse: { value: null }, uOffset: { value: 0.001 } },
    vertexShader: `varying vec2 vUv; void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }`,
    fragmentShader: `
        uniform sampler2D tDiffuse; uniform float uOffset; varying vec2 vUv;
        void main() {
            vec2 center = vec2(0.5); float dist = length(vUv - center) * 2.0;
            if (dist > 0.5) {
                float distortAmount = smoothstep(0.5, 1.0, dist) * uOffset; vec4 color;
                color.r = texture2D(tDiffuse, vUv + vec2(distortAmount, 0.0)).r;
                color.g = texture2D(tDiffuse, vUv).g;
                color.b = texture2D(tDiffuse, vUv - vec2(distortAmount, 0.0)).b;
                color.a = texture2D(tDiffuse, vUv).a;
                gl_FragColor = color;
            } else { gl_FragColor = texture2D(tDiffuse, vUv); }
        }`
};


// --- PostProcessingManager Class ---

export default class PostProcessingManager {
    constructor(renderer, scene, camera) {
        this.renderer = renderer;
        this.scene = scene;
        this.camera = camera;
        this.composer = null;
        this.effectPasses = {}; // Stores references to the actual pass instances
        this.selectedObjects = []; // For outline pass
        this.time = 0;
        this.isMobile = this._detectMobile();
        this.qualityLevel = this.isMobile ? 'low' : 'high'; // Default quality

        // Configuration for effects - holds enabled state and parameters
        this.effectsConfig = this._getDefaultEffectsConfig();

        console.log(`[PostProcessingManager] Initialized. Mobile: ${this.isMobile}, Quality: ${this.qualityLevel}`);
    }

    _detectMobile() {
       const mobileRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
       return mobileRegex.test(navigator.userAgent) || window.innerWidth <= 768;
    }

    _getDefaultEffectsConfig() {
        // Deep clone default params to avoid modifying the original
        const params = JSON.parse(JSON.stringify(DEFAULT_EFFECT_PARAMS));

        // Convert color strings/numbers back to THREE.Color objects where needed
        params.outline.visibleEdgeColor = new THREE.Color(params.outline.visibleEdgeColor);
        params.outline.hiddenEdgeColor = new THREE.Color(params.outline.hiddenEdgeColor);
        params.vignette.color = new THREE.Color(params.vignette.color);
        // Convert vectors from arrays if needed after JSON stringify/parse
        params.colorGrading.colorBalance = new THREE.Vector3().fromArray(Object.values(params.colorGrading.colorBalance));
        params.colorGrading.tint = new THREE.Vector3().fromArray(Object.values(params.colorGrading.tint));


        // Define initial enabled state (can be adjusted by quality level later)
        return {
            bloom:           { enabled: false, params: params.bloom },
            outline:         { enabled: false, params: params.outline }, // Outline often stylistic
            celShader:       { enabled: false, params: params.celShader }, // Specific look
            colorGrading:    { enabled: true, params: params.colorGrading }, // Useful for tuning
            vignette:        { enabled: false, params: params.vignette }, // Common effect
            filmGrain:       { enabled: !this.isMobile, params: params.filmGrain },
            chromaticAberration: { enabled: !this.isMobile, params: params.chromaticAberration },
            fxaa:            { enabled: true, params: {} } // FXAA has no custom params here
        };
    }

    /**
     * Creates the EffectComposer and adds enabled passes based on config.
     * Should be called after the manager is constructed.
     */
    setup() {
        console.log("[PostProcessingManager] Setting up composer...");
        this.composer = new EffectComposer(this.renderer);
        this.effectPasses = {}; // Clear previous passes if setup is called again

        // 1. Base Render Pass (Always first)
        const renderPass = new RenderPass(this.scene, this.camera);
        this.composer.addPass(renderPass);

        // 2. Outline Pass (Renders selected objects, usually runs early)
        if (this.effectsConfig.outline.enabled) {
            this._addOutlinePass();
        }

        // 3. Bloom Pass
        if (this.effectsConfig.bloom.enabled) {
            this._addBloomPass();
        }

        // 4. Stylistic/Color Correction Passes
        if (this.effectsConfig.celShader.enabled) {
             this._addCelShaderPass();
        }
        if (this.effectsConfig.colorGrading.enabled) {
            this._addColorGradingPass();
        }
        if (this.effectsConfig.vignette.enabled) {
            this._addVignettePass();
        }
        if (this.effectsConfig.filmGrain.enabled) {
            this._addFilmGrainPass();
        }
        if (this.effectsConfig.chromaticAberration.enabled) {
            this._addChromaticAberrationPass();
        }

        // 5. Anti-Aliasing Pass (Usually last or near last)
        if (this.effectsConfig.fxaa.enabled) {
           this._addFXAAPass();
        }

        // Apply initial quality settings to parameters
        this.applyQualitySettings(this.qualityLevel);

        console.log("[PostProcessingManager] Composer setup complete.");
        return this.composer;
    }

    // --- Private Helper methods for adding passes ---

    _addBloomPass() {
        const params = this.effectsConfig.bloom.params;
        const pass = new UnrealBloomPass(
            new THREE.Vector2(window.innerWidth, window.innerHeight),
            params.strength,
            params.radius,
            params.threshold
        );
        this.composer.addPass(pass);
        this.effectPasses.bloom = pass;
        console.log("[PostProcessingManager] Added UnrealBloomPass");
    }

    _addOutlinePass() {
        const params = this.effectsConfig.outline.params;
        const pass = new OutlinePass(
             new THREE.Vector2(window.innerWidth, window.innerHeight),
             this.scene, this.camera, this.selectedObjects
        );
        pass.edgeStrength = params.edgeStrength;
        pass.edgeGlow = params.edgeGlow;
        pass.edgeThickness = params.edgeThickness;
        pass.pulsePeriod = params.pulsePeriod;
        pass.visibleEdgeColor = params.visibleEdgeColor;
        pass.hiddenEdgeColor = params.hiddenEdgeColor;
        this.composer.addPass(pass);
        this.effectPasses.outline = pass;
         console.log("[PostProcessingManager] Added OutlinePass");
    }

     _addCelShaderPass() {
        const params = this.effectsConfig.celShader.params;
        const pass = new ShaderPass(CelShader);
        pass.uniforms.uQuantizeSteps.value = params.quantizeSteps;
        this.composer.addPass(pass);
        this.effectPasses.celShader = pass;
         console.log("[PostProcessingManager] Added CelShaderPass");
    }

    _addColorGradingPass() {
        const params = this.effectsConfig.colorGrading.params;
        const pass = new ShaderPass(ColorGradingShader);
        // Set initial uniform values from config
        for (const key in params) {
             if (pass.uniforms[key]) {
                  pass.uniforms[key].value = params[key];
             }
        }
        this.composer.addPass(pass);
        this.effectPasses.colorGrading = pass;
         console.log("[PostProcessingManager] Added ColorGradingPass");
    }

     _addVignettePass() {
        const params = this.effectsConfig.vignette.params;
        const pass = new ShaderPass(VignetteShader);
        for (const key in params) {
             if (pass.uniforms[key]) {
                  pass.uniforms[key].value = params[key];
             }
        }
        this.composer.addPass(pass);
        this.effectPasses.vignette = pass;
        console.log("[PostProcessingManager] Added VignettePass");
    }

     _addFilmGrainPass() {
        const params = this.effectsConfig.filmGrain.params;
        const pass = new ShaderPass(FilmGrainShader);
        for (const key in params) {
             if (pass.uniforms[key]) {
                  pass.uniforms[key].value = params[key];
             }
        }
        pass.uniforms.uTime.value = this.time; // Initialize time
        this.composer.addPass(pass);
        this.effectPasses.filmGrain = pass;
        console.log("[PostProcessingManager] Added FilmGrainPass");
    }

     _addChromaticAberrationPass() {
        const params = this.effectsConfig.chromaticAberration.params;
        const pass = new ShaderPass(ChromaticAberrationShader);
         for (const key in params) {
             if (pass.uniforms[key]) {
                  pass.uniforms[key].value = params[key];
             }
        }
        this.composer.addPass(pass);
        this.effectPasses.chromaticAberration = pass;
        console.log("[PostProcessingManager] Added ChromaticAberrationPass");
    }

     _addFXAAPass() {
        const pass = new ShaderPass(FXAAShader);
        this.composer.addPass(pass);
        this.effectPasses.fxaa = pass;
        this._updateFxaaResolution(); // Set initial resolution
        console.log("[PostProcessingManager] Added FXAAPass");
    }


    // --- Public Methods ---

    /**
     * Select objects to apply outline effect to.
     * @param {THREE.Object3D[]} objects - Array of objects to outline.
     */
    selectObjectsForOutline(objects) {
        this.selectedObjects = objects;
        if (this.effectPasses.outline && this.effectsConfig.outline.enabled) {
            this.effectPasses.outline.selectedObjects = this.selectedObjects;
        }
    }

    _updateFxaaResolution() {
        if (this.effectPasses.fxaa && this.effectsConfig.fxaa.enabled) {
            const pixelRatio = this.renderer.getPixelRatio();
            this.effectPasses.fxaa.material.uniforms['resolution'].value.x = 1 / (window.innerWidth * pixelRatio);
            this.effectPasses.fxaa.material.uniforms['resolution'].value.y = 1 / (window.innerHeight * pixelRatio);
        }
    }

    onWindowResize() {
         if (!this.composer) return;

        const width = window.innerWidth;
        const height = window.innerHeight;
        const pixelRatio = this.renderer.getPixelRatio();

        this.composer.setSize(width, height);
        this.composer.setPixelRatio(pixelRatio);

        this._updateFxaaResolution();

        if (this.effectPasses.outline && this.effectsConfig.outline.enabled) {
            this.effectPasses.outline.resolution.set(width, height);
        }
        if (this.effectPasses.bloom && this.effectsConfig.bloom.enabled) {
            this.effectPasses.bloom.setSize(width, height);
            // Re-apply quality settings which might adjust bloom resolution for mobile
             this.applyQualitySettings();
        }

        // Re-check mobile status on resize
        const wasMobile = this.isMobile;
        this.isMobile = this._detectMobile();
        if (wasMobile !== this.isMobile) {
             console.log(`[PostProcessingManager] Device type changed. Mobile: ${this.isMobile}`);
             this.applyQualitySettings(); // Re-apply settings based on new device type
        }
    }

    /**
     * Adjusts effect parameters and enabled states based on quality level and device type.
     * @param {string} [level=this.qualityLevel] - 'low', 'medium', or 'high'.
     */
    applyQualitySettings(level = this.qualityLevel) {
        console.log(`[PostProcessingManager] Applying quality settings: ${level}`);
        this.qualityLevel = level;
        const isLow = level === 'low';
        const isMedium = level === 'medium';

        // --- Enable/Disable Effects based on Level ---
        this.effectsConfig.filmGrain.enabled = !isLow && !this.isMobile;
        this.effectsConfig.chromaticAberration.enabled = !isLow && !this.isMobile;
        this.effectsConfig.bloom.enabled = !isLow; // Often disable bloom entirely on low
        this.effectsConfig.outline.enabled = this.effectsConfig.outline.enabled; // Keep user setting for outline
        this.effectsConfig.celShader.enabled = this.effectsConfig.celShader.enabled; // Keep user setting
        this.effectsConfig.vignette.enabled = !isLow; // Vignette is relatively cheap
        this.effectsConfig.colorGrading.enabled = true; // Usually always enabled for look tuning
        this.effectsConfig.fxaa.enabled = true; // Usually always enabled

        // --- Adjust Parameters based on Level ---
        const bloomPass = this.effectPasses.bloom;
        if (bloomPass) {
            bloomPass.enabled = this.effectsConfig.bloom.enabled;
            if (bloomPass.enabled) {
                bloomPass.strength = isMedium ? 0.4 : DEFAULT_EFFECT_PARAMS.bloom.strength; // Lower strength on medium
                // Adjust resolution for mobile regardless of level (if bloom is enabled)
                const targetWidth = this.isMobile ? Math.round(window.innerWidth * 0.6) : window.innerWidth;
                const targetHeight = this.isMobile ? Math.round(window.innerHeight * 0.6) : window.innerHeight;
                 // Check if setSize exists (it should for UnrealBloomPass)
                 if (typeof bloomPass.setSize === 'function') {
                    bloomPass.setSize(targetWidth, targetHeight);
                 } else {
                    // Fallback for older versions or different bloom passes might be needed
                    // bloomPass.resolution?.set(targetWidth, targetHeight);
                 }

            }
        }

        const vignettePass = this.effectPasses.vignette;
        if (vignettePass) {
             vignettePass.enabled = this.effectsConfig.vignette.enabled;
             if (vignettePass.enabled) {
                 vignettePass.uniforms.uVignetteIntensity.value = isMedium ? 0.3 : DEFAULT_EFFECT_PARAMS.vignette.intensity;
             }
        }

        const celShaderPass = this.effectPasses.celShader;
        if (celShaderPass) {
            celShaderPass.enabled = this.effectsConfig.celShader.enabled;
             if (celShaderPass.enabled) {
                 celShaderPass.uniforms.uQuantizeSteps.value = isLow ? 2.0 : (isMedium ? 3.0 : DEFAULT_EFFECT_PARAMS.celShader.quantizeSteps);
             }
        }

         const filmGrainPass = this.effectPasses.filmGrain;
        if (filmGrainPass) {
             filmGrainPass.enabled = this.effectsConfig.filmGrain.enabled;
        }

         const chromaticAberrationPass = this.effectPasses.chromaticAberration;
        if (chromaticAberrationPass) {
            chromaticAberrationPass.enabled = this.effectsConfig.chromaticAberration.enabled;
        }

        // Rebuild composer if necessary? For now, just toggle pass 'enabled' flag
        // If passes need to be actually added/removed, the logic is more complex.
         console.log("[PostProcessingManager] Updated pass enabled states based on quality.");
    }

    /**
     * Updates time-dependent effects. Call this in your main animation loop.
     * @param {number} deltaTime - Time elapsed since the last frame.
     */
    update(deltaTime) {
        this.time += deltaTime;
        // Update time uniforms for relevant passes
        if (this.effectPasses.filmGrain && this.effectsConfig.filmGrain.enabled) {
            this.effectPasses.filmGrain.uniforms.uTime.value = this.time;
        }
        // Add other time-based updates here if needed
    }

    /**
     * Renders the scene through the EffectComposer.
     */
    render() {
        if (this.composer) {
            this.composer.render();
        } else {
             console.warn("[PostProcessingManager] Render called but composer is not set up.");
             // Fallback to direct render if composer failed?
             // this.renderer.render(this.scene, this.camera);
        }
    }

    /**
     * Toggles an effect on or off and potentially rebuilds the composer.
     * @param {string} effectName - Key from effectsConfig (e.g., 'bloom', 'vignette').
     * @param {boolean} isEnabled - True to enable, false to disable.
     */
    toggleEffect(effectName, isEnabled) {
        if (this.effectsConfig[effectName] !== undefined) {
             if (this.effectsConfig[effectName].enabled !== isEnabled) {
                 console.log(`[PostProcessingManager] Toggling ${effectName} to ${isEnabled}`);
                 this.effectsConfig[effectName].enabled = isEnabled;
                 // Option 1: Simple enable/disable flag on the pass (works for most built-in passes)
                 if (this.effectPasses[effectName]) {
                    this.effectPasses[effectName].enabled = isEnabled;
                 }
                 // Option 2: Rebuild the composer (more robust if pass order matters or shaders change drastically)
                 // this.setup(); // Uncomment this if simple enabling/disabling doesn't work correctly
             }
        } else {
             console.warn(`[PostProcessingManager] Attempted to toggle unknown effect: ${effectName}`);
        }
    }

    /**
     * Updates a parameter for a specific effect.
     * @param {string} effectName - The key of the effect (e.g., 'bloom', 'colorGrading').
     * @param {string} paramName - The name of the parameter (e.g., 'strength', 'uBrightness').
     * @param {*} value - The new value for the parameter.
     */
    updateEffectParameter(effectName, paramName, value) {
        if (this.effectsConfig[effectName] && this.effectPasses[effectName]) {
            const pass = this.effectPasses[effectName];
            // Update parameter in config
            this.effectsConfig[effectName].params[paramName] = value;
            // Update uniform if it exists
            if (pass.uniforms && pass.uniforms[paramName]) {
                // Handle THREE specific types
                if (pass.uniforms[paramName].value instanceof THREE.Color) {
                    pass.uniforms[paramName].value.set(value);
                } else if (pass.uniforms[paramName].value instanceof THREE.Vector3 && value instanceof THREE.Vector3) {
                     pass.uniforms[paramName].value.copy(value);
                } else {
                     pass.uniforms[paramName].value = value;
                }
                // console.log(`[PostProcessingManager] Updated ${effectName}.${paramName} to ${value}`);
            } else if (pass[paramName] !== undefined) {
                 // Handle direct properties like bloomPass.strength
                 pass[paramName] = value;
                 // console.log(`[PostProcessingManager] Updated ${effectName}.${paramName} to ${value}`);
            } else {
                 console.warn(`[PostProcessingManager] Parameter '${paramName}' not found on pass for effect '${effectName}'.`);
            }
        } else {
             console.warn(`[PostProcessingManager] Cannot update parameter for unknown or disabled effect: ${effectName}`);
        }
    }

     // Add dispose method for cleanup
     dispose() {
          console.log("[PostProcessingManager] Disposing...");
          // If passes have dispose methods, call them
          for (const passName in this.effectPasses) {
               const pass = this.effectPasses[passName];
               if (pass && typeof pass.dispose === 'function') {
                    pass.dispose();
               }
          }
          this.effectPasses = {};
          this.composer = null; // Allow garbage collection
     }
}